/**
 * AddressClassifier.ts — F3.07a
 * Clasificador de geocodificabilidad pre-cascada.
 *
 * Determina si una infraestructura merece entrar en la cascada L0-L5
 * o si es una pérdida de tiempo (placeholder, red genérica, etc.).
 *
 * Diseñado como utilidad pura sin estado ni efectos secundarios.
 * La integración en el Orchestrator se hace en F3.07b.
 *
 * @module services/geocoding/orchestrator/AddressClassifier
 * @version 1.0.0
 * @date 2026-02-14
 */

import { esPlaceholder } from '../../../lib/coordinate/detectors';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Categorías de geocodificabilidad.
 *
 * - GEOCODIFICABLE: dirección postal, topónimo, parcela → cascada completa
 * - LINEAL_NOMBRADO: carretera/río con identificador → solo L1 NGA/DERA
 * - RED_GENERICA: "Término municipal", "Red de…" → NO geocodificar
 * - PLACEHOLDER: "Indicar", vacío, "S/D" → NO geocodificar
 */
export type GeocodabilityCategory =
  | 'GEOCODIFICABLE'
  | 'LINEAL_NOMBRADO'
  | 'RED_GENERICA'
  | 'PLACEHOLDER'
  | 'SOCIAL_SIN_DATOS'; // L3-3: albergue/refugio/nave ferial sin dirección → cotejo por nombre

/** Resultado de la clasificación de geocodificabilidad */
export interface GeocodingClassification {
  /** Categoría asignada */
  category: GeocodabilityCategory;
  /** Motivo legible, apto para exportación y UI */
  reason: string;
  /** true → no entrar en cascada L0-L5 */
  skipCascade: boolean;
  /** Score máximo alcanzable (0 para skip, undefined para cascada normal) */
  maxScore?: number;
}

// ============================================================================
// PATRONES — Extraídos de análisis empírico de 22 documentos PTEL
// ============================================================================

/**
 * Patrones de direcciones que representan redes genéricas o ámbitos
 * municipales completos. No tienen ubicación puntual geocodificable.
 *
 * Caso real Colomera: "Término municipal" como dirección de "Caminos Rurales".
 */
const RED_GENERICA_PATTERNS: readonly RegExp[] = [
  /^t[eé]rmino\s+municipal$/i,
  /^todo\s+el\s+(t[eé]rmino|municipio)$/i,
  /^casco\s+urbano$/i,
  /^red\s+de\s+/i,
  /^red\s+(viaria|el[eé]ctrica|hidr[aá]ulica)/i,
  /^alumbrado\s+p[uú]blico$/i,
  /^saneamiento/i,
  /^abastecimiento/i,
  /^distribuci[oó]n/i,
  /^recogida\s+de\s+/i,
  /^varias\s+ubicaciones$/i,
  /^disperso/i,
  /^todo\s+el\s+n[uú]cleo$/i,
  /^n[uú]cleo\s+urbano$/i,
];

/**
 * Nombres de infraestructura que indican redes genéricas sin punto concreto.
 * Se evalúan sobre el campo `nombre`, no sobre `direccion`.
 */
const RED_GENERICA_NOMBRE_PATTERNS: readonly RegExp[] = [
  /^caminos?\s+rurales?$/i,
  /^red\s+(viaria|el[eé]ctrica|hidráulica|de\s+comunicaci[oó]n)/i,
  /^alumbrado/i,
  /^saneamiento/i,
  /^abastecimiento\s+(de\s+agua)?$/i,
  /^recogida\s+de\s+(basuras?|residuos)/i,
  /^alcantarillado/i,
];

/**
 * Patrones de elementos lineales con identificador concreto.
 * Tienen nombre propio pero no dirección postal → geocodificar
 * solo vía L1 NGA/DERA con centroide representativo.
 *
 * Caso real Colomera: "Carretera A-4076", "Carretera GR-3420".
 */
const LINEAL_NOMBRADO_PATTERNS: readonly RegExp[] = [
  // Carreteras con código: A-4076, GR-3420, N-432, AP-7, etc.
  /^(autov[ií]a|autopista|carretera|ctra\.?)\s+[A-Z]{1,3}[-\s]?\d{1,5}/i,
  // Ríos, arroyos, barrancos con nombre
  /^(r[ií]o|arroyo|barranco|rambla|ca[ñn]ada)\s+\w/i,
  // Veredas, cañadas, sendas con nombre
  /^(vereda|ca[ñn]ada|senda|cordel|colada)\s+(del?|de\s+l[ao]s?)?\s*\w/i,
  // Líneas eléctricas con código
  /^l[ií]nea\s+(el[eé]ctrica|de\s+(alta|media|baja)\s+tensi[oó]n)/i,
  // Gasoductos, oleoductos
  /^(gasoducto|oleoducto|acueducto)/i,
];

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Clasifica una infraestructura según su geocodificabilidad.
 *
 * Orden de evaluación (primera coincidencia gana):
 * 1. PLACEHOLDER — dirección vacía o genérica ("Indicar", "S/D", etc.)
 * 2. RED_GENERICA — dirección o nombre indican red difusa sin punto concreto
 * 3. SOCIAL_SIN_DATOS — infra social sin dirección → cotejo por nombre (L3-3)
 * 4. LINEAL_NOMBRADO — nombre identifica elemento lineal con identificador
 * 5. GEOCODIFICABLE — resto de casos, entra en cascada completa
 * 4. GEOCODIFICABLE — todo lo demás
 *
 * @param direccion - Campo dirección de la infraestructura (puede ser vacío)
 * @param nombre - Nombre de la infraestructura
 * @param tipoElemento - Tipo de elemento declarado en el documento PTEL (opcional)
 * @returns Clasificación con categoría, motivo y decisión de skip
 */
export function classifyForGeocoding(
  direccion: string | null | undefined,
  nombre: string | null | undefined,
  tipoElemento?: string | null,
): GeocodingClassification {
  const dir = normalize(direccion);
  const nom = normalize(nombre);
  const tipo = normalize(tipoElemento);

  const dirIsEmpty = dir === '' || esPlaceholder(dir);
  const nomIsEmpty = nom === '';

  // --- 1. PLACEHOLDER ---
  // Ambos campos vacíos o placeholder → nada que geocodificar
  if (dirIsEmpty && nomIsEmpty) {
    return skip('PLACEHOLDER', dir === '' ? 'Sin dirección ni nombre' : `Dirección placeholder: "${dir}"`);
  }

  // --- 2. RED_GENERICA ---
  // Dirección indica ámbito difuso
  if (!dirIsEmpty && matchesAny(dir, RED_GENERICA_PATTERNS)) {
    return skip('RED_GENERICA', `Dirección genérica: "${dir}"`);
  }

  // Nombre indica red difusa (y dirección es vacía/placeholder)
  if (!nomIsEmpty && matchesAny(nom, RED_GENERICA_NOMBRE_PATTERNS)) {
    // Si además la dirección es real (no placeholder), podría geocodificarse
    if (!dirIsEmpty) {
      // dir="Avd. Benalúa, 48" + nom="Alumbrado público" → geocodificable por dirección
    } else {
      return skip('RED_GENERICA', `Infraestructura de red genérica: "${nom}"`);
    }
  }

  // Tipo de elemento indica red genérica (y sin dirección útil)
  if (tipo !== '' && dirIsEmpty && matchesAny(tipo, RED_GENERICA_PATTERNS)) {
    // Solo si nombre tampoco es un lineal nombrado concreto
    if (nomIsEmpty || !matchesAny(nom, LINEAL_NOMBRADO_PATTERNS)) {
      return skip('RED_GENERICA', `Tipo de elemento genérico: "${tipo}"`);
    }
  }

  // --- 3. SOCIAL_SIN_DATOS (L3-3) ---
  // Infraestructura social (albergue, refugio, nave ferial, local social)
  // sin dirección postal → cascada de cotejo por nombre vía case SOCIAL (L3-1).
  // No es PLACEHOLDER (tiene nombre válido), pero requiere ruta especial.
  // Guard doble: dirIsEmpty + patrón de nombre social → evita falsos positivos
  // cuando hay dirección postal real (caso más frecuente en producción).
  const SOCIAL_SIN_DATOS_PATTERNS: readonly RegExp[] = [
    /\balbergue\b/i,
    /\brefugio\b/i,
    /\bnave\s+(ferial|municipal)\b/i,
    /\blocal\s+social\b/i,
    /\bcasa\s+(de\s+(la\s*)?)?acogida\b/i,
    /\balojamiento\s+(de\s+)?emergencia\b/i,
  ];

  if (dirIsEmpty && !nomIsEmpty && matchesAny(nom, SOCIAL_SIN_DATOS_PATTERNS)) {
    return {
      category: 'SOCIAL_SIN_DATOS',
      reason: `Infraestructura social sin dirección: "${nom}"`,
      skipCascade: false,  // SÍ entra en cascada vía trySpecializedGeocoder(SOCIAL)
      maxScore: 65,        // techo de confianza sin validación postal
    };
  }

  // --- 4. LINEAL_NOMBRADO ---
  // Nombre identifica elemento lineal con código/identificador
  if (!nomIsEmpty && matchesAny(nom, LINEAL_NOMBRADO_PATTERNS)) {
    return {
      category: 'LINEAL_NOMBRADO',
      reason: `Elemento lineal nombrado: "${nom}"`,
      skipCascade: false, // Entra en cascada pero solo niveles L1 NGA/DERA
      maxScore: 65,
    };
  }

  // --- 5. GEOCODIFICABLE ---
  return {
    category: 'GEOCODIFICABLE',
    reason: !dirIsEmpty ? `Dirección: "${dir}"` : `Nombre: "${nom}"`,
    skipCascade: false,
  };
}

// ============================================================================
// HELPERS INTERNOS
// ============================================================================

/** Normaliza input a string limpio */
function normalize(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value).trim();
}

/** Comprueba si un texto coincide con algún patrón de la lista */
function matchesAny(text: string, patterns: readonly RegExp[]): boolean {
  return patterns.some(p => p.test(text));
}

/** Crea resultado de skip (PLACEHOLDER o RED_GENERICA) */
function skip(
  category: 'PLACEHOLDER' | 'RED_GENERICA',
  reason: string,
): GeocodingClassification {
  return { category, reason, skipCascade: true, maxScore: 0 };
}
